import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMount from '../../../../../../../common/hooks/useMount';
import Button from '../../../../../../../common/widgets/Button';
import Icons from '../../../../../../../common/widgets/Icons';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import { CardFormFactor, CardType } from '../../../../../enums';
import { rewriteImageUrl } from '../../../../../helpers';
import { AvailableCardTypeModel } from '../../../../../models/Cards';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';
import { CardDetailsDialog } from './CardDetailsDialog';

interface CardTypeSelectorProps {
  cardTypes?: AvailableCardTypeModel[];
  setCardTypes: (cardTypes: AvailableCardTypeModel[]) => void;
  selectCardType: (
    cardType: CardType,
    thumbnail?: string,
    mainImage?: string,
  ) => void;
}

interface CardTypeSelectorState {
  showErrorPanel?: boolean;
}

export const CardTypeSelector = (props: CardTypeSelectorProps) => {
  const { selectCardType, cardTypes, setCardTypes } = props;
  const { t: translate } = useTranslation();
  const [state, setState] = useState<CardTypeSelectorState>({});
  const [cardFormFactor, setCardFormFactor] = useState<CardFormFactor[]>([]);

  const loadCardTypes = async (abortController?: AbortController) => {
    const resp = await cardService.getAvailableCardTypesToBuy({
      signal: abortController?.signal,
    });
    if (!resp.hasErrors) {
      setCardTypes(resp.data);
    } else if (!abortController?.signal.aborted) {
      setState({ showErrorPanel: true });
    }

    const type = await cardService.getFormFactorOptionType(CardType.Meal, {
      signal: abortController?.signal,
    });

    if (type.hasErrors) {
      if (!abortController?.signal.aborted) {
        setState({ showErrorPanel: true });
      }
    } else {
      setCardFormFactor(type.data);
    }
  };

  useMount(abortController => {
    if (!cardTypes) {
      loadCardTypes(abortController);
    }
  });

  return (
    <Loader
      loading={!cardTypes}
      loader={<LoadingSpinner />}
      showErrorPanel={state.showErrorPanel}
    >
      {!!cardTypes && (
        <>
          <h1 className="card-step-title">
            {translate(resources.employeeCards.buy.step1.title)}
          </h1>
          <ul className="card-type-selector-list">
            {cardTypes.map(e => (
              <CardTypeTile
                key={e.cardTypeId}
                cardType={e.cardTypeId}
                cardFormFactor={cardFormFactor}
                thumbnail={e.thumbnail}
                onOrderClick={(event: any) => {
                  event?.preventDefault();
                  event?.stopPropagation();
                  selectCardType(e.cardTypeId, e.thumbnail, e.mainImage);
                }}
              />
            ))}
          </ul>
        </>
      )}
    </Loader>
  );
};

type CardTypeTileProps = {
  thumbnail?: string;
  cardType: CardType;
  cardFormFactor: CardFormFactor[];
  onOrderClick: (event: any) => void;
};

type CardTypeTileState = {
  isDetailsDialogVisible: boolean;
};

const CardTypeTile = (props: CardTypeTileProps) => {
  const { thumbnail, cardType, cardFormFactor, onOrderClick } = props;
  const { t: translate } = useTranslation();
  const { name, info1, info2 } = getCardTypeDetails(cardType);
  const [state, setState] = useState<CardTypeTileState>({
    isDetailsDialogVisible: false,
  });
  const toggleDialog = (e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    setState(
      produce(d => {
        d.isDetailsDialogVisible = !d.isDetailsDialogVisible;
      }),
    );
  };
  return (
    <>
      <li className="cursor-pointer" onClick={onOrderClick}>
        <div className="cts-image">
          <img src={rewriteImageUrl(thumbnail, 416, 234)} />
        </div>
        <div className="cts-details">
          <div className="cts-name cursor-pointer" onClick={onOrderClick}>
            {translate(name)}
          </div>
          <ul className="cts-info">
            <li>{translate(info1)}</li>
            <li>{translate(info2)}</li>
          </ul>
          <div className="cts-actions">
            <Button className="btn btn-solid" onClick={onOrderClick}>
              <span className="btn-text">{translate(resources.orderNow)}</span>
            </Button>
            <button className="btn-link" onClick={toggleDialog}>
              <span className="btn-icon">
                <Icons.ArrowButton />
              </span>
              <span className="btn-text">
                {translate(resources.seeDetails)}
              </span>
            </button>
          </div>
        </div>
      </li>
      {state.isDetailsDialogVisible && (
        <CardDetailsDialog
          cardFormFactor={cardFormFactor}
          onCloseDialog={() => toggleDialog()}
          onOrderClick={onOrderClick}
        />
      )}
    </>
  );
};

const getCardTypeDetails = (
  cardType: CardType,
): { name: string; info1: string; info2: string } => {
  switch (cardType) {
    case CardType.Culture:
      return {
        name: resources.employeeCards.cardTypes.culture,
        info1: resources.employeeCards.buy.step1.cultureCard.info1,
        info2: resources.employeeCards.buy.step1.cultureCard.info2,
      };
    case CardType.Gift:
      return {
        name: resources.employeeCards.cardTypes.gift,
        info1: resources.employeeCards.buy.step1.giftCard.info1,
        info2: resources.employeeCards.buy.step1.giftCard.info2,
      };
    case CardType.Meal:
      return {
        name: resources.employeeCards.cardTypes.meal,
        info1: resources.employeeCards.buy.step1.mealCard.info1,
        info2: resources.employeeCards.buy.step1.mealCard.info2,
      };
    case CardType.Vacation:
      return {
        name: resources.employeeCards.cardTypes.vacation,
        info1: resources.employeeCards.buy.step1.vacationCard.info1,
        info2: resources.employeeCards.buy.step1.vacationCard.info2,
      };
    case CardType.Blue:
      return {
        name: resources.employeeCards.cardTypes.blue,
        info1: resources.employeeCards.buy.step1.blueCard.info1,
        info2: resources.employeeCards.buy.step1.blueCard.info2,
      };
    default:
      return { name: '', info1: '', info2: '' };
  }
};
